import { Link } from 'react-router-dom';
import { useStateContext } from '../../hooks/useStateContext';
import { STATES } from '../../utils/states';

export default function Footer() {
  // Footer shows on every page — scope renew link to the visitor's selected state.
  const { state } = useStateContext();

  // Courses column lists every state from the registry (single source of truth),
  // each linking to its own catalog. New states auto-appear here when added to states.js.
  const allStates = Object.values(STATES);

  // Approvals column shows a badge per LIVE (regulator-approved) state. Today that's
  // only Arizona, so it renders identically to before; extra states appear as they go live.
  const approvedStates = allStates.filter((s) => s.live);
  const approvalHeading =
    approvedStates.length === 1
      ? `${approvedStates[0].name} ${approvedStates[0].regulator}`
      : 'State Approvals';

  return (
    <footer style={styles.footer}>
      <div className="container">
        <div style={styles.grid}>
          <div>
            <div style={styles.brand}>
              <div style={styles.logoMark}>SE</div>
              <div>
                <div style={styles.brandName}>Steady Education</div>
                <div style={styles.brandSub}>Online Real Estate CE</div>
              </div>
            </div>
            <p style={styles.tagline}>
              Complete your real estate continuing education requirements online — on your schedule.
            </p>
          </div>

          <div>
            <h4 style={styles.colTitle}>Courses</h4>
            <ul style={styles.links}>
              {allStates.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}/courses`} style={styles.link}>{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 style={styles.colTitle}>Help</h4>
            <ul style={styles.links}>
              <li><Link to={`/${state.slug}/how-to-renew`} style={styles.link}>How to Renew</Link></li>
              <li><Link to="/about" style={styles.link}>About Us</Link></li>
              <li><Link to="/contact" style={styles.link}>Contact</Link></li>
              <li><Link to="/terms" style={styles.link}>Terms of Service</Link></li>
            </ul>
          </div>

          <div>
            <h4 style={styles.colTitle}>{approvalHeading}</h4>
            {approvedStates.map((s, i) => (
              <div key={s.slug} style={i > 0 ? styles.approvalBlock : undefined}>
                <p style={styles.adreText}>
                  Courses approved by the {s.regulatorFull || s.regulator} ({s.regulator}) for CE credit.
                </p>
                <div style={styles.badge}>
                  <span>✓</span> {s.regulator} Approved
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.bottom}>
          <p style={styles.copyright}>
            © {new Date().getFullYear()} Steady Education. All rights reserved.
          </p>
          <p style={styles.copyright}>
            Online real estate CE courses for licensed professionals.
          </p>
        </div>
      </div>
    </footer>
  );
}

const styles = {
  footer: {
    background: 'var(--slate-900)',
    color: 'white',
    paddingTop: 56,
    paddingBottom: 32,
    marginTop: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 40,
    paddingBottom: 40,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  logoMark: {
    width: 38, height: 38,
    background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
    borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 800, fontSize: 14,
  },
  brandName: { fontSize: 15, fontWeight: 700, color: 'white' },
  brandSub: { fontSize: 11, color: 'var(--teal-300)' },
  tagline: { fontSize: 14, color: 'var(--slate-400)', lineHeight: 1.6, maxWidth: 280 },
  colTitle: { fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' },
  links: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
  link: { fontSize: 14, color: 'var(--slate-400)', textDecoration: 'none', transition: 'color 0.15s' },
  adreText: { fontSize: 13, color: 'var(--slate-400)', marginBottom: 12, lineHeight: 1.5 },
  approvalBlock: { marginTop: 20 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(13,148,136,0.2)', color: 'var(--teal-300)',
    border: '1px solid rgba(13,148,136,0.3)',
    padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600,
  },
  bottom: { paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  copyright: { fontSize: 13, color: 'var(--slate-500)' },
};
